import { Button, Image, Input, Modal, Select, Table, TableColumnsType } from "antd";
import { SearchOutlined, MergeCellsOutlined } from "@ant-design/icons";
import NextIcon from "components/Icons/NextIcon";
import PreviousIcon from "components/Icons/PreviousIcon";
import { sortIcon } from "components/Table";
import Action from "./components/Action";
import useFleet from "hooks/fleets/useFleet";
import { IFleet } from "models/fleet";
import { IDriver } from "models/driver";
import { useState } from "react";
import { updateFleetApi } from "api/fleet";
import { useNotificationContext } from "contexts/NotificationContext";
import { useDataLoadingContext } from "contexts/DataLoadingContext";

export default function Fleet() {
  const {
    data: { data: fleets, loading, page, per_page, search, total, category },
    handleSearch,
    handlePageChange,
    handleCategoryChange,
  } = useFleet();

  const { showSuccess, showError } = useNotificationContext();
  const { loadFleets } = useDataLoadingContext();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isMergeModalVisible, setIsMergeModalVisible] = useState(false);
  const [targetCategory, setTargetCategory] = useState<string>("");
  const [isMerging, setIsMerging] = useState(false);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleBulkMerge = async () => {
    if (!targetCategory) {
      showError("Please select a category");
      return;
    }

    setIsMerging(true);
    try {
      // For each selected fleet, update the category
      // In a real app, you'd want a bulk API endpoint, but we'll loop for now if not available
      const promises = selectedRowKeys.map((id) =>
        updateFleetApi(id as string, { category: targetCategory })
      );

      await Promise.all(promises);
      showSuccess(`Successfully merged ${selectedRowKeys.length} fleets into ${targetCategory}`);
      setIsMergeModalVisible(false);
      setSelectedRowKeys([]);
      loadFleets();
    } catch (error) {
      showError("Failed to merge fleets");
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <div className="w-full">
      <div className="w-full px-6 flex flex-wrap-reverse justify-between items-center bg-white py-4 rounded-t-lg border-b">
        <div className="flex gap-4 items-center">
          <Select
            className="w-48"
            placeholder="Filter by Category"
            allowClear
            value={category || undefined}
            onChange={handleCategoryChange}
            options={[
              { label: "Engine", value: "Engine" },
              { label: "EV", value: "EV" },
              { label: "Megala", value: "Megala" },
              { label: "Proto", value: "Proto" },
              { label: "Bajaj", value: "Bajaj" },
              { label: "Proto Megala", value: "Proto Megala" },
              { label: "Megala Engine", value: "Megala Engine" },
              { label: "Megala EV", value: "Megala EV" },
              { label: "Proto Engine", value: "Proto Engine" },
              { label: "Proto EV", value: "Proto EV" },
            ]}
          />
          {selectedRowKeys.length > 0 && (
            <Button
              type="primary"
              icon={<MergeCellsOutlined />}
              onClick={() => setIsMergeModalVisible(true)}
              className="bg-blue-600 hover:bg-blue-700 flex items-center"
            >
              Merge {selectedRowKeys.length} Selected Fleets
            </Button>
          )}
          <p className="text-gray-500 text-sm">
            {selectedRowKeys.length > 0 ? `${selectedRowKeys.length} items selected` : `${total} Fleets Total`}
          </p>
        </div>
        <Input
          className="max-w-72 w-full h-auto px-4 py-2 text-base border border-[rgb(0,0,0,0.1)] rounded-3xl"
          prefix={<SearchOutlined className="mr-2" />}
          placeholder="Search..."
          onChange={handleSearch}
          value={search}
        />
      </div>
      <div className="w-full mt-2 px-4">
        <Table
          rowSelection={rowSelection}
          loading={loading}
          columns={columns}
          dataSource={fleets}
          rowKey={(record) => record.id}
          onChange={handlePageChange}
          pagination={{
            total,
            pageSize: per_page,
            current: page,
            nextIcon: (
              <span className="flex items-center justify-center w-8 h-8 text-2xl">
                <NextIcon />
              </span>
            ),
            prevIcon: (
              <span className="flex items-center justify-center w-8 h-8 text-2xl">
                <PreviousIcon />
              </span>
            ),
          }}
        />
      </div>

      <Modal
        title="Merge Fleets / Bulk Update Category"
        open={isMergeModalVisible}
        onOk={handleBulkMerge}
        onCancel={() => setIsMergeModalVisible(false)}
        confirmLoading={isMerging}
        okText="Merge & Update"
        cancelText="Cancel"
      >
        <div className="py-4">
          <p className="mb-4">Select the target category for the {selectedRowKeys.length} selected vehicles:</p>
          <Select
            className="w-full"
            placeholder="Select Category"
            onChange={(value) => setTargetCategory(value)}
            options={[
              { label: "Engine", value: "Engine" },
              { label: "EV", value: "EV" },
              { label: "Megala", value: "Megala" },
              { label: "Proto", value: "Proto" },
              { label: "Bajaj", value: "Bajaj" },
              { label: "Proto Megala", value: "Proto Megala" },
              { label: "Megala Engine", value: "Megala Engine" },
              { label: "Megala EV", value: "Megala EV" },
              { label: "Proto Engine", value: "Proto Engine" },
              { label: "Proto EV", value: "Proto EV" },
            ]}
          />
        </div>
      </Modal>
    </div>
  );
}

const columns: TableColumnsType<IFleet> = [
  {
    title: "Fleet Photo",
    dataIndex: "image",
    render: (image: string) => (
      <Image
        height={60}
        width={60}
        className="rounded object-cover"
        src={image}
        preview={false}
      />
    ),
  },
  {
    title: "Plate Number",
    dataIndex: "plate_number",
    sortIcon,
    sorter: (a, b) => a.plate_number.localeCompare(b.plate_number),
  },
  {
    title: "Category",
    dataIndex: "category",
    sortIcon,
    render: (category: string) => (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
        category === "EV" ? "bg-green-100 text-green-700" : 
        category === "Engine" ? "bg-blue-100 text-blue-700" :
        category?.includes("Megala") ? "bg-purple-100 text-purple-700" :
        "bg-gray-100 text-gray-700"
      }`}>
        {category || "Uncategorized"}
      </span>
    ),
    sorter: (a, b) => (a.category || "").localeCompare(b.category || ""),
  },
  {
    title: "Brand",
    dataIndex: "brand",
    sortIcon,
    sorter: (a, b) => a.brand.localeCompare(b.brand),
  },
  {
    title: "Model",
    sortIcon,
    dataIndex: "model",
    sorter: (a, b) => a.model.localeCompare(b.model),
  },
  {
    title: "Driver",
    dataIndex: "driver",
    sorter: (a, b) =>
      `${a.driver.first_name} ${b.driver.middle_name} ${a.driver.last_name}`.localeCompare(
        `${b.driver.first_name} ${b.driver.middle_name} ${b.driver.last_name}`
      ),
    sortIcon,
    render: (value: IDriver) => (
      <p>
        {value.first_name} {value.middle_name} {value.last_name}
      </p>
    ),
  },
  {
    title: "Action",
    render: (value: IFleet) => <Action fleet={value} />,
  },
];
